<template>
  <div class="modal fade" id="save-plan-dialog" tabindex="-1" role="dialog" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content p-5">
        <div class="form-group">
          <h3 class="mb-3"><strong>Save Plan</strong></h3>
          <p><label for="plannedTrailName" class="mb-1"><small><strong>Hike name</strong></small></label></p>
          <input type="text" v-model="title" class="form-control rounded-pill mb-3 p-2" id="plannedTrailName"
                 :placeholder="defaultTitle">

          <hr class="my-3">

          <div class="row mb-3">
            <div class="col">
              <p><label class="mb-1"><small><strong>Difficulty</strong></small></label></p>
              <button 
                class="difficulty-select-button rounded-pill p-2"
                :style="{ color: difficultyColor }"
                @click.prevent="showDifficultyDialog"
              >
                <strong>{{ difficulty }}</strong>
                <span v-if="isDifficultyOverridden" class="override-indicator" title="Manually set">✎</span>
              </button>
            </div>
          </div>

          <hr class="my-3">

          <p><label class="mb-1" for="plannedTrailDesc"><small><strong>Hike description</strong></small></label></p>
          <textarea rows="3" type="text" v-model="description" class="form-control rounded-lg p-2 mb-3" id="plannedTrailDesc" placeholder="Some details about the route"></textarea>

          <hr class="my-3">

          <div class="row">
            <div class="col">
              <p><label class="mb-1"><small><strong>Hike date</strong></small></label></p>
              <input
                :min="minDate"
                type="datetime-local"
                class="text-muted rounded-pill pl-2 mr-2 flex-fill"
                id="plannedTrailTime"
                :value="localizedPlannedToHikeOn"
                @input="updatePlannedToHikeOn"
                >
            </div>
          </div>

          <hr class="my-3">

          <p><label for="plannedTrailImg" class="form-label mb-1"><small><strong>Add images</strong></small></label></p>
          <form class="custom-file">
            <button class="btn btn-secondary" @click="triggerFileInput($event)">Choose Files</button>
            <input multiple="multiple" ref="fileInput" accept=".jpg, .jpeg, .png" id="plannedTrailImg" type="file" class="custom-file-upload" style="display: none;" @change="handleFileInputChange"/>
            <div v-if="allPhotos.length > 0" class="thumbnails mt-2 mb-2">
              <small class="text-muted d-block mb-1" style="font-size: 0.7rem;">Drag to reorder &middot; First photo will be the cover</small>
              <div
                v-for="(photo, index) in allPhotos"
                :key="photo.clientId"
                class="thumbnail square"
                :class="{
                  'dragging': !photo.isUploading && draggingPhotoIndex === index,
                  'drag-over': !photo.isUploading && dragOverPhotoIndex === index,
                  'uploading': photo.isUploading,
                }"
                :draggable="!photo.isUploading"
                @dragstart="!photo.isUploading && onPhotoDragStart($event, index)"
                @dragend="onPhotoDragEnd"
                @dragover.prevent="!photo.isUploading && onPhotoDragOver($event, index)"
                @drop.prevent="!photo.isUploading && onPhotoDrop(index)"
              >
                <img :src="photo.previewUrl" :alt="`photo-${index}`" class="thumbnail-image" style="pointer-events: none;" />
                <div v-if="photo.isUploading" class="upload-overlay">
                  <div class="upload-spinner"></div>
                </div>
                <template v-else>
                  <div v-if="index === 0" class="cover-badge">Cover photo</div>
                  <button @click="removePhoto($event, photo.clientId, photo.isExisting)" class="delete-button">x</button>
                </template>
              </div>
            </div>
          </form>

          <hr class="my-3">

          <div class="row">

            <div class="col-12">
              <div class="accordion" id="accordionActivities">
                <div class="accordion-item">
                  <h2 class="accordion-header">
                    <button class="accordion-button collapsed px-0" type="button" data-bs-toggle="collapse" data-bs-target="#collapseActivities" aria-expanded="true" aria-controls="collapseActivities">
                      <small><strong>Add activity tags</strong></small>
                    </button>
                  </h2>
                  <div id="collapseActivities" class="accordion-collapse collapse" data-bs-parent="#accordionActivities">
                    <div class="accordion-body px-0">
                      <div class="tag-list">
                        <button
                          v-for="tag in availableActivityTags"
                          :key="`activity-${tag}`"
                          type="button"
                          class="tag-pill"
                          :class="{ active: isActivityTagSelected(tag) }"
                          :aria-pressed="isActivityTagSelected(tag)"
                          @click="toggleActivityTag(tag)"
                        >
                          {{ formatTag(tag) }}
                        </button>
                        <p v-if="!availableActivityTags.length" class="text-muted small mb-0">No activity tags available.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <hr class="my-3">

          <div class="row">

            <div class="col-12">
              <div class="accordion" id="accordionFeatures">
                <div class="accordion-item">
                  <h2 class="accordion-header">
                    <button class="accordion-button collapsed px-0" type="button" data-bs-toggle="collapse" data-bs-target="#collapse" aria-expanded="true" aria-controls="collapse">
                      <small><strong>Add feature tags</strong></small>
                    </button>
                  </h2>
                  <div id="collapse" class="accordion-collapse collapse" data-bs-parent="#accordionFeatures">
                    <div class="accordion-body px-0">
                      <div class="tag-list">
                        <button
                          v-for="tag in availableFeatureTags"
                          :key="`feature-${tag}`"
                          type="button"
                          class="tag-pill"
                          :class="{ active: isFeatureTagSelected(tag) }"
                          :aria-pressed="isFeatureTagSelected(tag)"
                          @click="toggleFeatureTag(tag)"
                        >
                          {{ formatTag(tag) }}
                        </button>
                        <p v-if="!availableFeatureTags.length" class="text-muted small mb-0">No feature tags available.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <hr class="my-3">

          <div class="row">

            <div class="col-12">
              <div class="accordion" id="accordionTags">
                <div class="accordion-item">
                  <h2 class="accordion-header">
                    <button class="accordion-button collapsed px-0" type="button" data-bs-toggle="collapse" data-bs-target="#collapseTags" aria-expanded="true" aria-controls="collapseTags">
                      <small><strong>Add tags</strong></small>
                    </button>
                  </h2>
                  <div id="collapseTags" class="accordion-collapse collapse" data-bs-parent="#accordionTags">
                    <div class="accordion-body px-0">
                      <div class="input-group mb-3">
                        <input
                          v-model="newCustomTag"
                          type="text"
                          class="form-control"
                          placeholder="Create a tag"
                          maxlength="50"
                          aria-label="Create a tag"
                          @keydown.enter.prevent="addCustomTag"
                        >
                        <button
                          type="button"
                          class="btn btn-secondary"
                          :disabled="!newCustomTag.trim()"
                          @click="addCustomTag"
                        >
                          Add
                        </button>
                      </div>
                      <div class="tag-list">
                        <button
                          v-for="tag in customTagOptions"
                          :key="`custom-${tag}`"
                          type="button"
                          class="tag-pill"
                          :class="{ active: isCustomTagSelected(tag) }"
                          :aria-pressed="isCustomTagSelected(tag)"
                          @click="toggleCustomTag(tag)"
                        >
                          {{ tag }}
                        </button>
                        <p v-if="!customTagOptions.length" class="text-muted small mb-0">Type above to create your first tag.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <hr class="my-3">

          <p><label for="" class="mb-1"><small><strong>Who can view</strong></small></label></p>
          <div class="input-group mb-3 text-muted">
            <p class="p-2 bg-dark w-100 rounded-pill">
              <select
                class="custom-select text-muted w-100"
                id="plannedTrailPrivacy"
                @change="privacyChanged"
                :disabled="!isEmailConfirmed"
              >
                <template v-if="isEmailConfirmed">
                  <template v-if="this.public">
                    <option value="1" selected>Everyone can view</option>
                    <option value="2">Only I can view</option>
                  </template>
                  <template v-else>
                    <option value="1">Everyone can view</option>
                    <option value="2" selected>Only I can view</option>
                  </template>
                </template>
                <template v-else>
                  <option value="2" selected>Only I can view</option>
                </template>
              </select>
            </p>
          </div>

          <hr class="my-3">

          <div class="d-flex align-items-center justify-content-between mb-3" v-if="!isTrailsUser && isEmailConfirmed">
            <label class="mb-0">
              <small>Submit this hike to be made official</small>
            </label>
            <label class="toggle-switch">
              <input
                type="checkbox"
                v-model="localSubmitForOfficial"
                @change="handleSubmitForOfficialChange"
              >
              <span class="toggle-slider"></span>
            </label>
          </div>

          <div v-if="!isEmailConfirmed" class="card p-2 mb-2">
            <small class="d-block">
              You must confirm your email to make hikes public or request official status.
            </small>
            <button
              class="btn btn-link p-0"
              @click.prevent="resendConfirmationEmail"
              :disabled="resendConfirmationLoading"
            >
              <small>{{ resendConfirmationLoading ? 'Sending...' : 'Resend confirmation email' }}</small>
            </button>
            <small v-if="resendConfirmationStatus" class="d-block text-muted">{{ resendConfirmationStatus }}</small>
          </div>
          <p v-else-if="!isEmailConfirmed && !isTrailsUser" class="text-muted small mb-3">
            Confirm your email to request official status.
          </p>

          <!-- Trails user options -->
          <div v-if="isTrailsUser" class="trails-user-options p-3 mb-3" style="background: rgba(255, 193, 7, 0.1); border-radius: 8px; border: 1px solid rgba(255, 193, 7, 0.3);">
            <div class="mb-2">
              <small class="fw-bold" style="color: #ffc107;">
                <i class="fa fa-star"></i> Official Trails Account Options
              </small>
            </div>
            <div class="form-check mb-2">
              <input
                type="checkbox"
                class="form-check-input"
                id="makeOfficial"
                v-model="makeOfficial"
              >
              <label class="form-check-label text-white" for="makeOfficial">
                <small>Make this hike official immediately</small>
              </label>
            </div>
            <div class="form-check">
              <input
                type="checkbox"
                class="form-check-input"
                id="generateAiDescription"
                v-model="generateAiDescription"
              >
              <label class="form-check-label text-white" for="generateAiDescription">
                <small>Generate AI description after saving</small>
              </label>
            </div>
          </div>

          <hr class="my-3">

          <div class="d-flex">
            <button class="btn btn-secondary flex-fill mr-2 mt-3" data-bs-dismiss="modal">CANCEL
            </button>
            <button class="btn btn-primary flex-fill mt-3" @click="save">SAVE</button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- Difficulty Dialog -->
  <PlanTrailDifficultyDialog
    ref="difficultyDialog"
    :currentDifficulty="$store.state.planTrail.difficultyOverride"
    :calculatedDifficulty="calculatedDifficulty"
    @close=""
  />
</template>

<script>

import { generateID } from "../../../utils/helpers/IDGenerator";
import axiosClient from "../../../utils/helpers/axios_client";
import { Modal } from "bootstrap";
import PlanTrailDifficultyDialog from "./PlanTrailDifficultyDialog.vue";

export default {
  components: {
    PlanTrailDifficultyDialog
  },
  data() {
    const today = new Date(); // get today's date and time
    const isoString = today.toISOString().slice(0, -8); // convert to ISO 8601 string and remove the
    return {
      minDate: isoString,
      files: [],
      previewUrls: [],
      draggingPhotoIndex: null,
      dragOverPhotoIndex: null,
      resendConfirmationLoading: false,
      resendConfirmationStatus: '',
      localSubmitForOfficial: false,
      newCustomTag: '',
    };
  },
  mounted() {
    if (!this.isEmailConfirmed) {
      this.$store.state.planTrail.public = false;
      this.$store.commit('planTrail/setSubmitForOfficialApproval', false);
    }
    // Initialize local toggle state from store
    this.localSubmitForOfficial = this.$store.state.planTrail.submitForOfficialApproval || false;
  },
  watch: {
    // Sync store changes to local state
    'submitForOfficialApproval'(newVal) {
      this.localSubmitForOfficial = newVal;
    },
  },
  methods: {
    triggerFileInput(event) {
      event.preventDefault();
      this.$refs.fileInput.click();
    },
    handleFileInputChange(event) {
      const newFiles = Array.from(event.target.files);
      if (newFiles.length === 0) return;

      const newIds = newFiles.map(() => generateID());
      let loadedCount = 0;

      newFiles.forEach((file, i) => {
        this.files.push(file);
        const id = newIds[i];
        const reader = new FileReader();
        reader.onload = (e) => {
          this.previewUrls.push({ previewUrl: e.target.result, id });
          loadedCount++;
          if (loadedCount === newFiles.length) {
            this.$store.dispatch('planTrail/uploadImages', {
              files: newFiles,
              ids: newIds,
            });
          }
        };
        reader.readAsDataURL(file);
      });

      this.$refs.fileInput.value = "";
    },
    privacyChanged(event) {
      if (!this.isEmailConfirmed) {
        this.$store.state.planTrail.public = false;
        return;
      }
      const isPublic = event.target.value === '1';
      this.$store.state.planTrail.public = isPublic;

      // If switching to private, uncheck the submit for official approval
      if (!isPublic && this.localSubmitForOfficial) {
        this.localSubmitForOfficial = false;
        this.$store.commit('planTrail/setSubmitForOfficialApproval', false);
      }
    },
    handleSubmitForOfficialChange() {
      // If trying to turn on and privacy is not public, show confirmation
      if (this.localSubmitForOfficial && !this.public) {
        if (confirm('Hikes can only be submitted for official status when the privacy setting is public. Would you like to make this hike public?')) {
          // Switch to public
          this.$store.state.planTrail.public = true;
          // Update the select dropdown to reflect the change
          const selectElement = document.getElementById('plannedTrailPrivacy');
          if (selectElement) {
            selectElement.value = '1';
          }
          // Sync to store
          this.$store.commit('planTrail/setSubmitForOfficialApproval', true);
        } else {
          // User declined, revert the toggle
          this.localSubmitForOfficial = false;
        }
      } else {
        // Normal toggle behavior - sync to store
        this.$store.commit('planTrail/setSubmitForOfficialApproval', this.localSubmitForOfficial);
      }
    },
    async resendConfirmationEmail() {
      if (this.resendConfirmationLoading) return;
      this.resendConfirmationLoading = true;
      this.resendConfirmationStatus = '';
      try {
        const response = await axiosClient.post('/user/resend_confirmation');
        this.resendConfirmationStatus = response?.data?.message || 'Confirmation email sent.';
      } catch (e) {
        this.resendConfirmationStatus = 'Could not send confirmation email. Please try again.';
      } finally {
        this.resendConfirmationLoading = false;
      }
    },
    async save() {
      // Do not allow saving transport-only plans
      if (this.$store.getters['planTrail/isTransportOnlyPlan']) {
        alert('Transport-only plans cannot be saved. Please add a hiking segment before saving your plan.');
        return;
      }

      // Validate that the plan has at least one main route (route type)
      if (!this.$store.getters['planTrail/hasMainRoute']) {
        alert('Your plan must contain at least one route type segment. Please add a route segment before saving.');
        return;
      }

      // Validate that each hike day has at least one main route
      const dayValidation = this.$store.getters['planTrail/validateHikeDaysHaveMainRoute'];
      if (!dayValidation.valid) {
        const dayList = dayValidation.invalidDays.map(d => `Day ${d}`).join(', ');
        alert(`Each hike day must contain at least one route or transport segment. The following days only have alt or spur segments: ${dayList}`);
        return;
      }

      // Close the modal
      const modalElement = document.getElementById('save-plan-dialog');
      const modalInstance = Modal.getInstance(modalElement);
      if (modalInstance) {
        modalInstance.hide();
      }

      // Dispatch the save action
      await this.$store.dispatch('planTrail/save');
    },
    removePhoto(event, clientId, isExisting) {
      event.preventDefault();
      if (isExisting) {
        this.$store.commit('planTrail/removeExistingHikeImage', clientId);
        this.$store.commit('planTrail/markPhotoForRemoval', clientId);
      } else {
        const index = this.previewUrls.findIndex((p) => p.id === clientId);
        if (index !== -1) {
          const file = this.files[index];
          this.previewUrls.splice(index, 1);
          this.files.splice(index, 1);
          this.$store.commit('planTrail/unmarkPhotoForRemoval', clientId);
          this.$store.dispatch('planTrail/destroyImage', { id: clientId, file });
        }
      }
    },
    onPhotoDragStart(event, index) {
      this.draggingPhotoIndex = index;
      event.dataTransfer.effectAllowed = 'move';
    },
    onPhotoDragEnd() {
      this.draggingPhotoIndex = null;
      this.dragOverPhotoIndex = null;
    },
    onPhotoDragOver(event, index) {
      if (this.draggingPhotoIndex === index) return;
      this.dragOverPhotoIndex = index;
    },
    onPhotoDrop(dropIndex) {
      if (this.draggingPhotoIndex === null || this.draggingPhotoIndex === dropIndex) {
        this.dragOverPhotoIndex = null;
        return;
      }

      // Only reorder ready photos — uploading ones are not yet in photosIds
      // and must not be written there prematurely
      const readyPhotos = this.allPhotos.filter((p) => !p.isUploading);
      const visibleIds = readyPhotos.map((p) => p.clientId);
      const [moved] = visibleIds.splice(this.draggingPhotoIndex, 1);
      visibleIds.splice(dropIndex, 0, moved);

      // Keep removed photos appended so they're still sent with _destroy: true
      const removedIds = this.$store.state.planTrail.photosMarkedForRemoval;
      this.$store.commit('planTrail/reorderPhotos', [...visibleIds, ...removedIds]);

      this.draggingPhotoIndex = null;
      this.dragOverPhotoIndex = null;
    },
    updatePlannedToHikeOn(event) {
      this.$store.dispatch('planTrail/setPlannedToHikeOn', event.target.value);
    },
    toggleActivityTag(tag) {
      if (typeof tag !== 'string' || tag.length === 0) {
        return;
      }

      const updated = this.isActivityTagSelected(tag)
        ? this.selectedActivityTags.filter((value) => value !== tag)
        : [...this.selectedActivityTags, tag];

      this.$store.commit('planTrail/setSelectedActivityTags', updated);
    },
    toggleFeatureTag(tag) {
      if (typeof tag !== 'string' || tag.length === 0) {
        return;
      }

      const updated = this.isFeatureTagSelected(tag)
        ? this.selectedFeatureTags.filter((value) => value !== tag)
        : [...this.selectedFeatureTags, tag];

      this.$store.commit('planTrail/setSelectedFeatureTags', updated);
    },
    isActivityTagSelected(tag) {
      return this.selectedActivityTags.includes(tag);
    },
    isFeatureTagSelected(tag) {
      return this.selectedFeatureTags.includes(tag);
    },
    addCustomTag() {
      const tag = this.newCustomTag.trim();
      if (!tag) {
        return;
      }

      // Adding creates the tag AND selects it for this hike. Dedupe
      // case-insensitively to mirror the backend's force_lowercase storage.
      if (!this.isCustomTagSelected(tag)) {
        this.$store.commit('planTrail/setSelectedCustomTags', [...this.selectedCustomTags, tag]);
      }
      this.newCustomTag = '';
    },
    toggleCustomTag(tag) {
      if (typeof tag !== 'string' || tag.length === 0) {
        return;
      }

      const updated = this.isCustomTagSelected(tag)
        ? this.selectedCustomTags.filter((value) => value.toLowerCase() !== tag.toLowerCase())
        : [...this.selectedCustomTags, tag];

      this.$store.commit('planTrail/setSelectedCustomTags', updated);
    },
    isCustomTagSelected(tag) {
      return this.selectedCustomTags.some((value) => value.toLowerCase() === tag.toLowerCase());
    },
    formatTag(tag) {
      if (typeof tag !== 'string') {
        return '';
      }

      return tag
        .split('-')
        .filter((segment) => segment.length > 0)
        .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
        .join(' ');
    },
    showDifficultyDialog() {
      this.$nextTick(() => {
        if (this.$refs.difficultyDialog) {
          this.$refs.difficultyDialog.show();
        }
      });
    },
  },
  computed: {
    existingImages() {
      return this.$store.getters['planTrail/existingHikeImages'] || [];
    },
    allPhotos() {
      const photosIds = this.$store.state.planTrail.photosIds;
      const markedForRemoval = this.$store.state.planTrail.photosMarkedForRemoval;

      const ready = photosIds
        .filter((clientId) => !markedForRemoval.includes(clientId))
        .map((clientId) => {
          const existing = this.existingImages.find((e) => e.clientId === clientId);
          const newPhoto = this.previewUrls.find((p) => p.id === clientId);
          return {
            clientId,
            previewUrl: existing?.previewUrl || newPhoto?.previewUrl || '',
            isExisting: !!existing,
            isUploading: false,
          };
        })
        .filter((photo) => photo.previewUrl);

      const uploading = this.previewUrls
        .filter((p) => !photosIds.includes(p.id))
        .map((p) => ({
          clientId: p.id,
          previewUrl: p.previewUrl,
          isExisting: false,
          isUploading: true,
        }));

      return [...ready, ...uploading];
    },
    availableActivityTags() {
      return this.$store.state.planTrail.availableActivityTags;
    },
    availableFeatureTags() {
      return this.$store.state.planTrail.availableFeatureTags;
    },
    selectedActivityTags() {
      return this.$store.state.planTrail.selectedActivityTags;
    },
    selectedFeatureTags() {
      return this.$store.state.planTrail.selectedFeatureTags;
    },
    selectedCustomTags() {
      return this.$store.state.planTrail.selectedCustomTags;
    },
    availableCustomTags() {
      return this.$store.state.planTrail.availableCustomTags;
    },
    // Chips shown in the Tags section: the user's previously-created catalog plus
    // any freshly typed tags not yet in it, deduped case-insensitively.
    customTagOptions() {
      const seen = new Set();
      const options = [];
      [...this.availableCustomTags, ...this.selectedCustomTags].forEach((tag) => {
        const key = tag.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          options.push(tag);
        }
      });
      return options;
    },
    defaultTitle() {
      const today = new Date();
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      const dateStr = today.toLocaleDateString('en-US', options);
      return `Hike planned on ${dateStr}`;
    },
    title: {
      get() {
        return this.$store.state.planTrail.title || '';
      },
      set(value) {
        this.$store.commit('planTrail/setTitle', value);
      },
    },
    description: {
      get() {
        return this.$store.state.planTrail.description;
      },
      set(value) {
        this.$store.commit('planTrail/setDescription', value);
      },
    },
    localizedPlannedToHikeOn() {
      const value = this.$store.getters['planTrail/getLocalizedPlannedToHikeOn'];
      return value;
    },
    public: {
      get() {
        return this.$store.state.planTrail.public;
      },
      set(value) {
        if (!this.isEmailConfirmed) {
          this.$store.state.planTrail.public = false;
          return;
        }
        this.$store.state.planTrail.public = value;
      },
    },
    submitForOfficialApproval: {
      get() {
        return this.$store.state.planTrail.submitForOfficialApproval;
      },
      set(value) {
        this.$store.commit('planTrail/setSubmitForOfficialApproval', value);
      },
    },
    makeOfficial: {
      get() {
        return this.$store.state.planTrail.makeOfficial;
      },
      set(value) {
        this.$store.commit('planTrail/setMakeOfficial', value);
      },
    },
    generateAiDescription: {
      get() {
        return this.$store.state.planTrail.generateAiDescription;
      },
      set(value) {
        this.$store.commit('planTrail/setGenerateAiDescription', value);
      },
    },
    isTrailsUser() {
      // Show for trails@hiiker.co or any admin user
      return window.currentUserEmail === 'trails@hiiker.co' || window.currentUserIsAdmin === true;
    },
    isEmailConfirmed() {
      return !!(window.user && window.user.confirmed_at);
    },
    calculatedDifficulty() {
      const totalDistance = this.$store.getters['planTrail/totalDistance'];
      const totalAscent = this.$store.getters['planTrail/totalAscent'];
      const distanceKm = totalDistance / 1000;
      if (distanceKm === 0) return 'Easy';
      
      const elevationGainPerKm = totalAscent / distanceKm;
      
      if (elevationGainPerKm < 20) return 'Easy';
      if (elevationGainPerKm < 60) return 'Medium';
      if (elevationGainPerKm < 101) return 'Difficult';
      return 'Extra Difficult';
    },
    difficulty() {
      return this.$store.state.planTrail.difficultyOverride || this.calculatedDifficulty;
    },
    isDifficultyOverridden() {
      return this.$store.state.planTrail.difficultyOverride !== null;
    },
    difficultyColor() {
      switch (this.difficulty) {
        case 'Easy': return '#4CAF50';
        case 'Medium': return '#FF9800';
        case 'Difficult': return '#F44336';
        case 'Extra Difficult': return '#9C27B0';
        default: return '#999';
      }
    },
  },
};
</script>

<style scoped>

.dropdown-toggle {
  text-align: left;
}

.dropdown-toggle::after {
  float: right
}

.accordion-item{
  border: none;
}

.accordion-header, .accordion-button{
  background: transparent;
}

.accordion-button::after {
  background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23ffffff'><path fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/></svg>");
}

.thumbnails {
  white-space: nowrap; /* Prevent line breaks */
  overflow-x: auto; /* Enable horizontal scrolling */
}

.thumbnail {
  border: 1px solid #505150;
  padding: 4px;
  border-radius: 5px;
  display: inline-block;
  margin-right: 11px;
  width: 90px;
  height: 90px;
  position: relative;
  cursor: grab;
}

.thumbnail.dragging {
  opacity: 0.4;
}

.thumbnail.drag-over {
  border-color: #f5dd42;
}

.thumbnail.uploading {
  cursor: default;
}

.cover-badge {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(245, 221, 66, 0.92);
  color: #17181d;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 3px 0;
  text-align: center;
  pointer-events: none;
  border-radius: 0 0 3px 3px;
}

.upload-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
}

.upload-spinner {
  width: 22px;
  height: 22px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.thumbnail-image {
  max-width: 100px;
  max-height: 100px;
}

.delete-button {
    position: absolute;
    top: 0;
    right: 0;
    background: rgb(0 0 0 / 68%);
    color: #fff;
    font-size: 17px;
    padding: 2px 4px;
    border: none;
    cursor: pointer;
    height: 30px;
    width: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 500;
}

.difficulty-select-button {
  background: #212529;
  border: 1px solid #495057;
  border-radius: 0.25rem;
  padding: 0.375rem 0.75rem;
  width: 100%;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.difficulty-select-button:hover {
  background: #2c3034;
  border-color: #6c757d;
}

.override-indicator {
  font-size: 0.875rem;
  opacity: 0.8;
  margin-left: 0.5rem;
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.tag-pill {
  background: #212529;
  border: 1px solid #495057;
  border-radius: 999px;
  color: #f8f9fa;
  padding: 0.35rem 0.9rem;
  font-size: 0.875rem;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.tag-pill:hover,
.tag-pill:focus {
  background: #2c3034;
  border-color: #6c757d;
  color: #fff;
}

.tag-pill.active {
  background: #0d6efd;
  border-color: #0d6efd;
  color: #fff;
}

.tag-pill:focus {
  outline: none;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
}

/* Toggle Switch Styles */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 42px;
  height: 24px;
  margin-bottom: 0;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .4s;
  border-radius: 24px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: #17181d;
  transition: .4s;
  border-radius: 50%;
}

input:checked + .toggle-slider {
  background-color: #f5dd42;
}

input:checked + .toggle-slider:before {
  transform: translateX(18px);
}

</style>
